<script setup>
import { ref, watch } from 'vue';
import DeskCollapse from './DeskCollapse.vue';

const props = defineProps({
    activities: { type: Array, default: () => [] },
    labels: { type: Object, default: () => ({}) },
    expanded: { type: Boolean, default: undefined },
});

const emit = defineEmits(['update:expanded']);

const innerExpanded = ref(props.expanded !== undefined ? props.expanded : false);

watch(() => props.expanded, (value) => {
    if (value !== undefined) {
        innerExpanded.value = value;
    }
});

const toggle = () => {
    innerExpanded.value = !innerExpanded.value;
    emit('update:expanded', innerExpanded.value);
};

const activityChanges = (activity) => (Array.isArray(activity.changes) ? activity.changes : []);

const changeText = (change) => {
    if (typeof change === 'string') {
        return change;
    }
    if (!change || typeof change !== 'object') {
        return '';
    }
    const label = change.label || change.field || change.name || '';
    const from = change.from_value ?? change.from;
    const to = change.to_value ?? change.to;
    if (from !== undefined || to !== undefined) {
        return `${label ? `${label}: ` : ''}${from || '—'} → ${to || '—'}`;
    }
    return change.description || change.value || label;
};
</script>

<template>
    <section
        class="ticket-desk-panel ticket-desk-activity"
        :class="{ 'ticket-desk-panel--collapsed': !innerExpanded }"
        data-testid="ticket-desk-activity"
    >
        <button
            type="button"
            class="ticket-desk-panel-heading ticket-desk-panel-heading--toggle"
            :aria-expanded="innerExpanded"
            @click="toggle"
        >
            <h3>
                <span aria-hidden="true">↻</span>
                {{ labels.ticketActivity || labels.ticket_activity || labels.activity || 'Attività ticket' }}
            </h3>
            <span class="ticket-desk-panel-heading__side">
                <span class="ticket-desk-badge ticket-desk-badge--neutral">{{ activities.length }}</span>
                <span class="ticket-desk-chevron" aria-hidden="true">▾</span>
            </span>
        </button>
        <DeskCollapse :expanded="innerExpanded">
            <div class="ticket-desk-activity__list">
                <div v-if="activities.length" class="ticket-desk-audit-timeline">
                    <article
                        v-for="(activity, index) in activities"
                        :key="activity.id || index"
                        class="ticket-desk-audit-timeline__item"
                    >
                        <span class="ticket-desk-audit-timeline__dot" aria-hidden="true" />
                        <div class="ticket-desk-audit-timeline__row">
                            <div class="ticket-desk-audit-timeline__text">
                                <div>
                                    {{ activity.description }}
                                    <span class="ticket-desk-audit-timeline__actor">
                                        · {{ activity.actor_label || labels.systemActor || labels.ticket_activity_actor_system || 'Sistema' }}
                                    </span>
                                    <span
                                        v-if="activity.actor_type"
                                        class="ticket-desk-badge ticket-desk-badge--outline"
                                        style="margin-left: 6px;"
                                    >{{ activity.actor_type }}</span>
                                    <span v-if="(activity.from_value || activity.to_value) && !activityChanges(activity).length">
                                        · {{ activity.from_value || '—' }} → {{ activity.to_value || '—' }}
                                    </span>
                                </div>
                                <div
                                    v-if="activityChanges(activity).length || activity.reason"
                                    class="ticket-desk-audit-timeline__details"
                                >
                                    <span v-if="activity.from_value || activity.to_value">
                                        {{ activity.from_value || '—' }} → {{ activity.to_value || '—' }}
                                    </span>
                                    <span
                                        v-for="(change, changeIndex) in activityChanges(activity)"
                                        :key="change.id || changeIndex"
                                    >
                                        {{ changeText(change) }}
                                    </span>
                                    <span v-if="activity.reason">{{ activity.reason }}</span>
                                </div>
                            </div>
                            <time class="ticket-desk-audit-timeline__time">
                                {{ activity.created_at_label || activity.time }}
                            </time>
                        </div>
                    </article>
                </div>
                <p v-else class="ticket-desk-empty">{{ labels.noActivity || 'Nessuna attività registrata' }}</p>
            </div>
        </DeskCollapse>
    </section>
</template>
